/**
 * Aircraft limits and the maintenance release (MR): TJR's Daily Entry "max flight" label and estimated fuel, the
 * Daily Flight grid's Land and Prog TTIS columns, and the Summary page's Aircraft box and MR list (TJR inventory 02
 * §2.4, §7, §9, §10; FUNCTIONAL_SPEC §10).
 *
 * Formulas (verified against the F26048 Summary page of 14 Oct 2026, VH-JOZ):
 *   - Hours flown = survey + comp box + cals + ferry; external hours are another aircraft's (TJR FUN_006dd474).
 *   - Prog TTIS after an entry = TTIS at start + the aircraft's hours flown up to and including that entry
 *     (5085.9 + 4.8 = 5090.7 after the 8 Oct ferry).
 *   - MR hours remaining = MR interval − (TTIS now − TTIS at the current MR's issue), where TTIS now = TTIS at start
 *     + hours flown and the issue is the latest "New MR TTIS" of a daily entry, else TTIS at issue
 *     (220 − (5085.9 + 48.0 − 5070.3) = 156.4 h).
 *   - Estimated fuel = hours flown × fuel flow, rounded half to even as Delphi's Round (8.5 h × 217 L/h = 1844 L).
 *   - Maximum flight time = the type's maximum flight hours, else usable fuel ÷ fuel flow.
 */
import type { Aircraft, DailyEntry, FuelType, Project, Uuid } from './model.js';

/** TJR's default "Flag if TTIS rem <=" for a new project: 20 h (the F26048 setting). 0 means Ignore. */
export const DEFAULT_MR_WARN_H = 20;
/** TJR's "Flag if TTIS rem <=" choices in hours (0 is Ignore). */
export const MR_WARN_CHOICES_H: readonly number[] = [0, 5, 10, 15, 20];

/** Survey + comp box + cals + ferry hours: the time the aircraft flew on this entry. */
export function hoursFlown(
  e: Pick<DailyEntry, 'svy_hours' | 'cbx_hours' | 'cal_hours' | 'fry_hours'>,
): number {
  return e.svy_hours + e.cbx_hours + e.cal_hours + e.fry_hours;
}

const legacyText = (a: Pick<Aircraft, 'legacy_json'>, key: string): string => {
  const v = a.legacy_json[key];
  return typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '';
};

/** The aircraft's fuel: its own field, else the TJR aircraft record (012 f8: "0" avgas, "1" turbine). */
export function aircraftFuelType(a: Aircraft): FuelType | null {
  if (a.fuel_type) return a.fuel_type;
  const tjr = legacyText(a, 'fuel_type');
  return tjr === '0' ? 'avgas' : tjr === '1' ? 'turbine' : null;
}

/** The type's maximum flight hours, else usable fuel ÷ fuel flow; null when neither is known. */
export function maxFlightHours(
  a: Pick<Aircraft, 'max_flight_h' | 'legacy_json' | 'fuel_capacity_l' | 'fuel_flow_lph'>,
): number | null {
  if (a.max_flight_h != null && a.max_flight_h > 0) return a.max_flight_h;
  const tjr = Number(legacyText(a, 'max_flight_hrs'));
  if (legacyText(a, 'max_flight_hrs') !== '' && Number.isFinite(tjr) && tjr > 0) return tjr;
  const flow = a.fuel_flow_lph;
  if (a.fuel_capacity_l != null && a.fuel_capacity_l > 0 && flow != null && flow > 0) {
    return a.fuel_capacity_l / flow;
  }
  return null;
}

/** Round half to even (Delphi's `Round`, which TJR uses for whole litres). */
export function roundHalfEven(x: number): number {
  const r = Math.round(x);
  return Math.abs(x % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}

/** TJR's "Est:" fuel: hours flown × the aircraft's fuel flow in whole litres; null without a fuel flow. */
export function estimatedFuelL(hours: number, a: Aircraft | null): number | null {
  const flow = a?.fuel_flow_lph ?? null;
  if (flow === null || !(flow > 0) || !(hours >= 0)) return null;
  return roundHalfEven(hours * flow);
}

/** `HH:MM` → minutes after midnight; null when blank or invalid. */
function minutesOf(time: string | null): number | null {
  const m = time === null ? null : /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Landing time (TJR Daily Flight "Land") = take-off + hours flown: 07:00 + 2.9 h = 09:54. Null without take-off. */
export function landingTime(e: DailyEntry): string | null {
  const start = minutesOf(e.takeoff_time);
  if (start === null) return null;
  const end = (((start + Math.round(hoursFlown(e) * 60)) % 1440) + 1440) % 1440;
  return `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
}

/** Entries in flying order: date, take-off time (none last), flight number, then as stored. */
export function chronological(entries: readonly DailyEntry[]): DailyEntry[] {
  return entries
    .map((e, i) => ({ e, i }))
    .sort((x, y) => {
      const a = x.e;
      const b = y.e;
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      const ta = minutesOf(a.takeoff_time) ?? Number.MAX_SAFE_INTEGER;
      const tb = minutesOf(b.takeoff_time) ?? Number.MAX_SAFE_INTEGER;
      if (ta !== tb) return ta - tb;
      return (a.flt_num ?? 0) - (b.flt_num ?? 0) || x.i - y.i;
    })
    .map((x) => x.e);
}

const liveOf = (entries: readonly DailyEntry[], aircraftId: Uuid): DailyEntry[] =>
  entries.filter((e) => e.deleted_at === null && e.aircraft_id === aircraftId);

/**
 * Prog TTIS after each of the aircraft's entries: TTIS at start + its hours flown so far, by entry id. Empty when the
 * aircraft has no TTIS at start.
 */
export function progressiveTtis(a: Aircraft, entries: readonly DailyEntry[]): Map<Uuid, number> {
  const out = new Map<Uuid, number>();
  if (a.ttis_at_start_h === null) return out;
  let ttis = a.ttis_at_start_h;
  for (const e of chronological(liveOf(entries, a.id))) {
    ttis += hoursFlown(e);
    out.set(e.id, ttis);
  }
  return out;
}

/** One line of the MR list (MB 19 Oct: "Issued", TJR printed "Initial"): "Issued 5070.3", "Job Start 5085.9", then each renewal. */
export interface MrListItem {
  readonly label: string;
  readonly ttis_h: number;
  /** The daily entry that renewed the MR (renewals only). */
  readonly entry_id?: Uuid;
  readonly date?: string;
}

export type MrState = 'ok' | 'due' | 'overdue' | 'unknown';

export interface MrStatus {
  readonly aircraft_id: Uuid;
  readonly interval_h: number | null;
  /** TTIS at the current MR's issue: the latest renewal, else the aircraft's TTIS at issue. */
  readonly issue_ttis_h: number | null;
  readonly start_ttis_h: number | null;
  /** Hours flown on the project by this aircraft (survey + comp box + cals + ferry). */
  readonly hours_flown_h: number;
  readonly ttis_now_h: number | null;
  readonly hours_since_mr_h: number | null;
  /** MR interval − hours since the MR; null when the interval or a TTIS is missing. */
  readonly remaining_h: number | null;
  /** The project's "flag if TTIS remaining ≤" in hours (0 = Ignore). */
  readonly warn_h: number;
  /** `due` at or below the flag (never with Ignore), `overdue` below zero. */
  readonly state: MrState;
  readonly list: readonly MrListItem[];
}

/** The project's MR flag in hours: its setting, else 20 h; 0 means Ignore. */
export function mrWarnHours(project: Pick<Project, 'mr_warn_h'> | null | undefined): number {
  const v = project?.mr_warn_h;
  return v != null && Number.isFinite(v) && v >= 0 ? v : DEFAULT_MR_WARN_H;
}

/** The aircraft's MR status from its daily entries (a renewal is an entry's `new_mr_ttis_h`). */
export function mrStatus(
  a: Aircraft,
  entries: readonly DailyEntry[],
  warnH: number = DEFAULT_MR_WARN_H,
): MrStatus {
  const mine = chronological(liveOf(entries, a.id));
  const hours = mine.reduce((s, e) => s + hoursFlown(e), 0);
  const list: MrListItem[] = [];
  if (a.ttis_at_issue_h !== null) list.push({ label: 'Issued', ttis_h: a.ttis_at_issue_h });
  if (a.ttis_at_start_h !== null) list.push({ label: 'Job start', ttis_h: a.ttis_at_start_h });
  let issue = a.ttis_at_issue_h;
  for (const e of mine) {
    if (e.new_mr_ttis_h !== null && e.new_mr_ttis_h > 0) {
      issue = e.new_mr_ttis_h;
      list.push({ label: `New MR ${e.date}`, ttis_h: e.new_mr_ttis_h, entry_id: e.id, date: e.date });
    }
  }
  const now = a.ttis_at_start_h === null ? null : a.ttis_at_start_h + hours;
  const since = now === null || issue === null ? null : now - issue;
  const interval = a.mr_interval_h;
  const remaining = interval === null || since === null ? null : interval - since;
  const state: MrState =
    remaining === null
      ? 'unknown'
      : remaining < 0
        ? 'overdue'
        : warnH > 0 && remaining <= warnH
          ? 'due'
          : 'ok';
  return {
    aircraft_id: a.id,
    interval_h: interval,
    issue_ttis_h: issue,
    start_ttis_h: a.ttis_at_start_h,
    hours_flown_h: hours,
    ttis_now_h: now,
    hours_since_mr_h: since,
    remaining_h: remaining,
    warn_h: warnH,
    state,
    list,
  };
}

/**
 * TJR Summary "Aircraft" box: hours by type for one aircraft. TJR's "Cal Hours" there is comp box + cals (1.00 h on
 * 14 Oct 2026 for VH-JOZ, all of it the 10 Oct comp box hour); Teslyn shows the two apart (MB, decision 162, Q141)
 * and keeps TJR's combined figure as `tjr_cal_h` for the TJR figures.
 */
export interface AircraftHours {
  readonly aircraft_id: Uuid;
  readonly survey_h: number;
  readonly comp_box_h: number;
  readonly cal_h: number;
  readonly ferry_h: number;
  readonly external_h: number;
  /** TJR's "Cal Hours": comp box + cals (decision 162 keeps it for the TJR figures). */
  readonly tjr_cal_h: number;
  /** Survey + comp box + cals + ferry (48.0 h for VH-JOZ on 14 Oct). */
  readonly total_h: number;
}

/** TJR's "Cal Hours" of a set of hours: comp box plus calibration (decision 162). */
export function tjrCalHours(h: { readonly comp_box_h: number; readonly cal_h: number }): number {
  return h.comp_box_h + h.cal_h;
}

export function aircraftHours(a: Aircraft, entries: readonly DailyEntry[]): AircraftHours {
  const mine = liveOf(entries, a.id);
  const sum = (f: (e: DailyEntry) => number): number => mine.reduce((s, e) => s + f(e), 0);
  return {
    aircraft_id: a.id,
    survey_h: sum((e) => e.svy_hours),
    comp_box_h: sum((e) => e.cbx_hours),
    cal_h: sum((e) => e.cal_hours),
    ferry_h: sum((e) => e.fry_hours),
    external_h: sum((e) => e.ext_hours),
    tjr_cal_h: sum((e) => e.cbx_hours + e.cal_hours),
    total_h: sum(hoursFlown),
  };
}
